import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { CONVEYANCING_LOCATIONS } from "@/lib/conveyancing-locations";
import {
  FEES,
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  PENINSULA_ADDRESS,
  faqSchema,
} from "@/lib/firm";
import { PROBATE_FEES, PROBATE_LOCATIONS } from "@/lib/probate-locations";
import { WILLS_LOCATIONS, WILL_FEES } from "@/lib/wills-locations";

const SITE = "https://coastsidelaw.com.au";
const URL = `${SITE}/faqs`;

const TITLE = "Conveyancing, Wills & Estates FAQs — Queensland | Coastside Law";
const DESCRIPTION = `Answers to the questions Queensland buyers, sellers, will-makers and executors actually ask — cooling off, settlement, transfer duty, wills, enduring powers of attorney and probate. Fixed fees from ${FEES.sale}. Call ${FIRM.phone}.`;

/** Short, quotable summary written for AI answer engines and featured snippets. */
const ANSWER =
  "Coastside Law is a Queensland law practice at Golden Beach, Caloundra, with a second office at Scarborough on the Redcliffe peninsula. " +
  `Residential conveyancing is charged at a fixed professional fee of ${FEES.sale} including GST for a sale and ${FEES.purchase} including GST for a purchase, with a free contract review before you sign. ` +
  `Wills are ${WILL_FEES.will} including GST for a simple will, ${WILL_FEES.pack} for a will and enduring power of attorney pack and ${WILL_FEES.couples} for a couples pack. ` +
  `A straightforward application for a grant of probate is ${PROBATE_FEES.grant} including GST, and full estate administration from ${PROBATE_FEES.admin}. ` +
  "In Queensland the standard REIQ contract gives a buyer a five business day cooling-off period that ends at 5pm on the fifth day, with a 0.25% penalty of the purchase price if the buyer terminates during it; there is no cooling off on a sale by auction. " +
  "Settlement of a residential contract is usually 30 days and is now conducted electronically through PEXA. " +
  "A will must be in writing, signed by the will-maker and witnessed by two adults who do not benefit under it. " +
  "Probate is generally required in Queensland where the deceased held real property in their sole name or a bank or share balance above the institution's threshold, and the application cannot be filed until 14 days after the notice of intention to apply is advertised. " +
  "Every file at Coastside Law is handled by a solicitor rather than a conveyancing clerk.";

const VARIANTS: ReadonlyArray<string> = [
  "conveyancing FAQs Queensland",
  "wills and estates frequently asked questions QLD",
  "how long is the cooling off period in Queensland",
  "how much does conveyancing cost in Queensland",
  "do I need probate in Queensland",
  "how much does a will cost QLD",
  "what does a conveyancing solicitor do",
  "questions to ask a conveyancer before signing",
  "Caloundra conveyancing questions answered",
  "Sunshine Coast wills and probate FAQ",
];

const CONVEYANCING_FAQS = [
  {
    q: "How much does conveyancing cost in Queensland?",
    a: `Our fixed professional fee is ${FEES.sale} including GST for a residential sale and ${FEES.purchase} including GST for a residential purchase. Searches, registration fees and transfer duty are additional third-party or government charges, and we set them out in writing before you engage us so the total is not a surprise at settlement.`,
  },
  {
    q: "How long is the cooling-off period in Queensland?",
    a: "Five business days for a standard REIQ residential contract, ending at 5pm on the fifth business day after the buyer receives the fully signed contract. A buyer who terminates during that period forfeits 0.25% of the purchase price. There is no cooling-off period on a property bought at auction, or on a contract signed on the same day as a passed-in auction.",
  },
  {
    q: "Should I get the contract reviewed before I sign?",
    a: "Yes, and we review residential contracts free of charge before you sign. Once you have signed, the terms are set: finance and building and pest dates, the deposit, inclusions, disclosure and special conditions all become binding, and changing them afterwards depends entirely on the other side agreeing.",
  },
  {
    q: "How long does settlement take in Queensland?",
    a: "Thirty days from the contract date is the usual residential timeframe. Finance approval is commonly due at 14 or 21 days and building and pest at 7 to 14 days. Settlement happens electronically through PEXA, so nobody attends in person and funds and title move at the same moment.",
  },
  {
    q: "What is the difference between a solicitor and a conveyancer?",
    a: "In Queensland, only a solicitor can carry out conveyancing — the state does not license non-solicitor conveyancers. Some firms hand your file to a clerk with a solicitor's name on the letterhead. At Coastside Law a solicitor runs the file, so contract advice, special conditions and disputes are handled by the person doing the work.",
  },
  {
    q: "How much transfer duty will I pay?",
    a: "Transfer duty is a Queensland Government charge calculated on the purchase price or value. Concessions apply for a home you will live in and for eligible first home buyers, and they can be worth thousands. We calculate the duty and the concessions you qualify for before settlement and lodge the documents for you.",
  },
  {
    q: "What happens if my finance is not approved in time?",
    a: "Tell us before the finance date, not after. A buyer who is unconditional and cannot settle risks losing the deposit and being sued for the shortfall. Where the lender needs longer, we ask the seller for an extension in writing before the date passes — an extension requested afterwards has no contract to extend.",
  },
  {
    q: "What is seller disclosure and does it affect me?",
    a: "Since 1 August 2025 a Queensland seller must give the buyer a disclosure statement and prescribed certificates before the buyer signs. Get it wrong and the buyer may terminate at any time before settlement. We prepare the disclosure statement for sellers and check it for buyers as part of the contract review.",
  },
  {
    q: "Can I move in before settlement?",
    a: "Only with the seller's written agreement, and it is riskier than most people expect — you take on the property before you own it, insurance and damage become contentious, and the seller loses easy remedies. Where it is genuinely needed we document the arrangement properly rather than relying on a handshake at the open home.",
  },
  {
    q: "Do you act on off-the-plan and house-and-land contracts?",
    a: "Yes, including the Aura, Baringa, Nirimba and Bells Reach releases. Developer contracts carry sunset dates, disclosure plan variations, later community title arrangements and covenants on build times and materials, and they are not the standard REIQ contract. We read the whole document, including the schedules.",
  },
];

const WILLS_FAQS = [
  {
    q: "How much does a will cost?",
    a: `${WILL_FEES.will} including GST for a simple will, ${WILL_FEES.pack} for a will and enduring power of attorney pack, and ${WILL_FEES.couples} for a couples pack of mirror wills and enduring powers of attorney. Estate planning involving companies, trusts, self-managed super or a blended family is quoted separately. We store original wills in safe custody free of charge.`,
  },
  {
    q: "What makes a will valid in Queensland?",
    a: "It must be in writing, signed by the will-maker, and signed by two adult witnesses who are present at the same time and who do not benefit under the will. A beneficiary — or their spouse — who witnesses the will can lose their gift. The will-maker must also have testamentary capacity and be acting free of pressure.",
  },
  {
    q: "Is a DIY will kit or an online will good enough?",
    a: "Sometimes it is, and often it is not. The problems we see are wrong witnessing, gifts of assets the will-maker did not actually own personally, no substitute beneficiaries, and superannuation and jointly owned property assumed to pass under the will when they do not. Fixing those problems after death costs the estate far more than the will did.",
  },
  {
    q: "What is an enduring power of attorney and do I need one?",
    a: "It appoints someone to make financial and personal or health decisions for you if you lose capacity. Without one, your family may have to apply to QCAT for an administration or guardianship order, which takes months. For most adults it matters at least as much as the will, and for anyone in a retirement village or approaching aged care it is essential.",
  },
  {
    q: "Who should I appoint as my executor?",
    a: "Somebody organised, contactable and even-handed — commonly a spouse, an adult child or a trusted friend. Always name a substitute in case your first choice dies before you or declines the role. One executor with a substitute is cleaner than two who must agree on everything.",
  },
  {
    q: "Does my will cover superannuation and jointly owned property?",
    a: "Usually not. Superannuation is paid at the trustee's discretion unless a binding death benefit nomination is in place, and property held as joint tenants passes to the survivor by survivorship, outside the will altogether. We check how each asset is actually held before drafting, because that is where most homemade plans fail.",
  },
  {
    q: "How often should I update my will?",
    a: "After a marriage, separation or divorce, a death in the family, a new child or grandchild, a property purchase or sale, or a move interstate. Marriage generally revokes an earlier will in Queensland unless it was made in contemplation of that marriage, which surprises a great many people.",
  },
  {
    q: "Can someone contest my will?",
    a: "A spouse, child or dependant can bring a family provision application in Queensland, generally within nine months of death, with notice to the executor within six months. A will cannot be made contest-proof, but clear reasoning, appropriate structures such as testamentary trusts and contemporaneous file notes make a claim far harder to run.",
  },
];

const PROBATE_FAQS = [
  {
    q: "Do I need probate in Queensland?",
    a: "Usually where the deceased owned real property in their sole name, or held a bank, share or investment balance above the institution's threshold. Assets owned as joint tenants pass to the survivor without a grant. Where everything was jointly held and balances are small, a grant is often unnecessary — we tell you before you spend money on one.",
  },
  {
    q: "How much does probate cost?",
    a: `Our fixed professional fee for a straightforward application for a grant of probate is ${PROBATE_FEES.grant} including GST, plus the Supreme Court filing fee and advertising costs. Full estate administration — collecting assets, paying debts, transferring or selling property and distributing to beneficiaries — starts at ${PROBATE_FEES.admin} and is quoted on the estate.`,
  },
  {
    q: "How long does probate take in Queensland?",
    a: "The notice of intention to apply must be advertised and served on the Public Trustee, and the application cannot be filed until at least 14 days later. Registry processing is commonly three to six weeks after filing. Most straightforward estates obtain a grant within six to ten weeks, and administration and distribution follow over the months after that.",
  },
  {
    q: "What does an executor actually have to do?",
    a: "Locate the original will, identify and secure the assets, advertise and apply for the grant if one is needed, pay debts and any tax, keep proper accounts, and distribute to the beneficiaries. Executors are personally accountable to the beneficiaries, which is why most sensibly instruct a solicitor and have the estate pay the cost.",
  },
  {
    q: "What happens if someone dies without a will?",
    a: "The estate is distributed under the intestacy rules in the Succession Act 1981 (Qld), which set a fixed order — spouse, then children, then wider family — regardless of what the deceased may have intended. Somebody eligible must apply for letters of administration instead of probate, which takes longer and costs more than probate on a valid will.",
  },
  {
    q: "Can you act for an executor who lives interstate or overseas?",
    a: "Yes. A Queensland estate is administered through the Supreme Court of Queensland regardless of where the executor lives. We run those files by email, phone and electronic signing, and we regularly act for executors in other states and overseas without them travelling here.",
  },
  {
    q: "Can the estate's house be sold before the grant?",
    a: "It can be marketed and placed under contract, but the transfer cannot settle until the grant issues and the title is dealt with, so the contract needs a settlement date and conditions that reflect that. We coordinate the timing with the agent so the contract does not fall over waiting on the registry.",
  },
];

const FIRM_FAQS = [
  {
    q: "Where are your offices?",
    a: `Our principal office is at ${FIRM_ADDRESS}, and we have a second office at ${PENINSULA_ADDRESS} on the Redcliffe peninsula. ${FIRM.hours}. ${FIRM.membership}.`,
  },
  {
    q: "Do I have to come into the office?",
    a: "No. Most conveyancing, wills and estate files run by email, phone, video and electronic signing, and settlement itself is electronic. We are happy to see you in person at Golden Beach or Scarborough, and we make home and aged care visits for will signings where a client cannot travel.",
  },
  {
    q: "How do I get a fixed-fee quote?",
    a: `Call ${FIRM.phone} or ${MICHAEL_PHONE}, or send an enquiry through this site. Tell us the property or the type of matter and we will come back with the fixed professional fee, the likely third-party costs and the next step in writing.`,
  },
];

const ALL_FAQS = [...CONVEYANCING_FAQS, ...WILLS_FAQS, ...PROBATE_FAQS, ...FIRM_FAQS];

const SECTIONS = [
  { id: "conveyancing", heading: "Buying and selling property", items: CONVEYANCING_FAQS },
  { id: "wills", heading: "Wills, enduring powers of attorney and estate planning", items: WILLS_FAQS },
  { id: "probate", heading: "Probate, executors and deceased estates", items: PROBATE_FAQS },
  { id: "firm", heading: "Working with Coastside Law", items: FIRM_FAQS },
];

export const Route = createFileRoute("/faqs")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(ALL_FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          url: URL,
          name: TITLE,
          description: DESCRIPTION,
          inLanguage: "en-AU",
          about: [
            { "@type": "Thing", name: "Conveyancing in Queensland" },
            { "@type": "Thing", name: "Wills and estate planning in Queensland" },
            { "@type": "Thing", name: "Probate and estate administration in Queensland" },
          ],
          publisher: {
            "@type": "LegalService",
            name: FIRM.name,
            telephone: FIRM.phone,
            email: FIRM.email,
            address: {
              "@type": "PostalAddress",
              streetAddress: FIRM.street,
              addressLocality: FIRM.suburb,
              addressRegion: FIRM.state,
              postalCode: FIRM.postcode,
              addressCountry: FIRM.country,
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "FAQs", item: URL },
          ],
        }),
      },
    ],
  }),
  component: FaqsPage,
});

function LinkGrid({
  heading,
  intro,
  links,
}: {
  heading: string;
  intro: string;
  links: ReadonlyArray<{ to: string; suburb: string; label: string }>;
}) {
  return (
    <div className="mt-12">
      <h3 className="font-serif text-2xl text-foreground">{heading}</h3>
      <p className="measure mt-2 text-sm leading-relaxed text-muted-foreground">{intro}</p>
      <ul className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {links.map((l) => (
          <li key={`${l.to}-${l.suburb}`}>
            <Link
              to={l.to}
              params={{ suburb: l.suburb }}
              className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-foreground transition-colors hover:border-accent hover:bg-muted"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FaqsPage() {
  const conveyancingLinks = CONVEYANCING_LOCATIONS.map((l) => ({
    to: "/conveyancing/$suburb",
    suburb: l.slug,
    label: `Conveyancing in ${l.name}`,
  }));
  const willsLinks = WILLS_LOCATIONS.map((l) => ({
    to: "/wills/$suburb",
    suburb: l.slug,
    label: `Wills in ${l.name}`,
  }));
  const probateLinks = PROBATE_LOCATIONS.map((l) => ({
    to: "/probate/$suburb",
    suburb: l.slug,
    label: `Probate in ${l.name}`,
  }));

  return (
    <>
      <PageHero
        eyebrow="Questions answered"
        title="Conveyancing, wills and estates FAQs"
        lead="The questions Queensland buyers, sellers, will-makers and executors actually ask us — answered plainly, with the fixed fee for each service and a link to the suburb page that covers your area."
      />

      <div className="mx-auto max-w-3xl px-5 py-16">
        <section className="rounded-sm border border-border bg-secondary/40 p-6">
          <h2 className="font-serif text-2xl text-foreground">The short answer</h2>
          <p className="measure mt-3 text-sm leading-relaxed text-muted-foreground">{ANSWER}</p>
        </section>

        <nav aria-label="FAQ sections" className="mt-10 flex flex-wrap gap-2">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="rounded-sm border border-border px-4 py-2 text-sm text-foreground transition-colors hover:bg-muted"
            >
              {s.heading}
            </a>
          ))}
        </nav>

        {SECTIONS.map((section) => (
          <section key={section.id} id={section.id} className="mt-14 scroll-mt-24">
            <h2 className="font-serif text-3xl text-foreground">{section.heading}</h2>
            <div className="mt-6">
              <FaqList items={section.items} />
            </div>
          </section>
        ))}

        <section className="mt-16">
          <h2 className="font-serif text-3xl text-foreground">Answers for your suburb</h2>
          <p className="measure mt-3 text-sm leading-relaxed text-muted-foreground">
            Each suburb page repeats these answers with the local detail that changes them —
            body corporate records, flood and overlay searches, developer contracts, retirement
            village contracts and the searches we run for that area.
          </p>

          <LinkGrid
            heading="Conveyancing by suburb"
            intro={`Fixed fees of ${FEES.sale} on a sale and ${FEES.purchase} on a purchase, both including GST, with a free contract review before you sign.`}
            links={conveyancingLinks}
          />
          <LinkGrid
            heading="Wills and enduring powers of attorney by suburb"
            intro={`Simple wills ${WILL_FEES.will} including GST, and will and enduring power of attorney packs from ${WILL_FEES.pack}.`}
            links={willsLinks}
          />
          <LinkGrid
            heading="Probate and estate administration by suburb"
            intro={`Grants of probate from ${PROBATE_FEES.grant} including GST, and full estate administration from ${PROBATE_FEES.admin}.`}
            links={probateLinks}
          />
        </section>

        <section className="mt-16">
          <h2 className="font-serif text-3xl text-foreground">Longer guides on these questions</h2>
          <ul className="mt-5 grid gap-2 sm:grid-cols-2">
            {[
              { to: "/wills-and-probate-queensland", label: "Wills and probate in Queensland" },
              { to: "/probate-queensland", label: "Probate in Queensland — process and costs" },
              { to: "/executor-queensland", label: "Being an executor in Queensland" },
              {
                to: "/how-to-appoint-an-executor",
                label: "How to appoint an executor",
              },
              {
                to: "/what-happens-when-someone-dies-queensland",
                label: "What happens when someone dies in Queensland",
              },
              { to: "/seller-disclosure", label: "Seller disclosure obligations" },
              { to: "/off-the-plan-contracts", label: "Off-the-plan and house-and-land contracts" },
              { to: "/cost-to-contest-a-will", label: "The cost of contesting a will" },
              { to: "/fees", label: "Our fixed fees in full" },
              { to: "/guides", label: "All guides and downloads" },
            ].map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-foreground transition-colors hover:border-accent hover:bg-muted"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-16">
          <h2 className="font-serif text-3xl text-foreground">Ways people search for this</h2>
          <ul className="measure mt-4 flex flex-wrap gap-2">
            {VARIANTS.map((v) => (
              <li
                key={v}
                className="rounded-sm border border-border px-3 py-1.5 text-xs text-muted-foreground"
              >
                {v}
              </li>
            ))}
          </ul>
        </section>

        <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} />

        <section className="mt-16">
          <EnquiryForm heading="Still have a question? Ask us" />
        </section>
      </div>

      <CtaBand
        title="Ask a solicitor, not a search box"
        body={`Call ${FIRM.phone} or ${MICHAEL_PHONE}, or send an enquiry and we will come back with a straight answer and a fixed fee.`}
      />
      <div className="pb-20" />
    </>
  );
}
